import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import { usersAPI, uploadAPI } from "../../services/api";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FiMapPin, FiCheck } from "react-icons/fi";

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: #f8f9fa;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const Header = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
  font-weight: bold;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const AvatarOverlay = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  background: #007bff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  border: 3px solid white;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const Name = styled.h1`
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 2.5rem;
`;

const Email = styled.p`
  margin: 0 0 0.5rem 0;
  color: #666;
  font-size: 1.1rem;
`;

const Role = styled.span`
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: capitalize;
`;

const Tabs = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e9ecef;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${(props) => (props.$active ? "#007bff" : "#666")};
  border-bottom: 2px solid
    ${(props) => (props.$active ? "#007bff" : "transparent")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #007bff;
  }
`;

const TabContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    background-color: #f8f9fa;
    color: #6c757d;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Button = styled.button`
  background: ${(props) =>
    props.$variant === "secondary" ? "#6c757d" : "#007bff"};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: fit-content;

  &:hover {
    background: ${(props) =>
      props.$variant === "secondary" ? "#5a6268" : "#0056b3"};
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const AddressCard = styled.div`
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  position: relative;

  &.default {
    border-color: #007bff;
    background: #f8f9ff;
  }
`;

const AddressHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 1rem;
`;

const AddressTitle = styled.h4`
  margin: 0;
  color: #333;
`;

const DefaultBadge = styled.span`
  background: #007bff;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  margin-left: 1rem;
`;

const AddressActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const AddressText = styled.p`
  margin: 0.5rem 0;
  color: #666;
  line-height: 1.5;
`;

const DietaryPreference = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const PreferenceName = styled.span`
  font-weight: 500;
  color: #333;
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: #c82333;
  }
`;

const AddButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #218838;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const LocationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;

  &:hover {
    background: #357ae8;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const LocationInfo = styled.div`
  background: #e8f5e9;
  border: 1px solid #4caf50;
  border-radius: 8px;
  padding: 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #2e7d32;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [dietaryPreferences, setDietaryPreferences] = useState([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    bio: "",
  });
  const [newAddress, setNewAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    country: "Guyana", // Always Guyana
    latitude: null,
    longitude: null,
    isDefault: false,
  });
  const [newPreference, setNewPreference] = useState("");
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
        bio: user.bio || "",
      });
    }
  }, [user]);

  useEffect(() => {
    loadAddresses();
    loadDietaryPreferences();
  }, []);

  const loadAddresses = async () => {
    try {
      const response = await usersAPI.getProfile();
      // Transform addresses from snake_case to camelCase
      const addresses = (response.data.data.user.addresses || []).map(
        (addr) => ({
          id: addr.id,
          type: addr.type || "home",
          street: addr.addressLine1,
          street2: addr.addressLine2,
          city: addr.city,
          state: addr.region,
          country: addr.country,
          isDefault: addr.isDefault,
          latitude: addr.latitude,
          longitude: addr.longitude,
        })
      );
      setAddresses(addresses);
    } catch (error) {
      console.error("Error loading addresses:", error);
    }
  };

  const loadDietaryPreferences = async () => {
    try {
      const response = await usersAPI.getDietaryPreferences();
      setDietaryPreferences(response.data.data || []);
    } catch (error) {
      console.error("Error loading dietary preferences:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setNewAddress((prev) => ({
          ...prev,
          latitude,
          longitude,
        }));
        toast.success("Location captured successfully!");
        setGettingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Failed to get location";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Location permission denied. Please enable location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out";
            break;
        }

        toast.error(errorMessage);
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await usersAPI.updateProfile(formData);

      // Transform snake_case to camelCase for the user data
      const userData = response.data.data.user;
      const transformedUserData = {
        id: userData.id,
        email: userData.email,
        firstName: userData.first_name,
        lastName: userData.last_name,
        phone: userData.phone,
        profileImage: userData.profile_image_url,
        updatedAt: userData.updated_at,
      };

      // Update the user context with the transformed data
      updateUser(transformedUserData);
      // Also update the form data to reflect the changes
      setFormData({
        firstName: transformedUserData.firstName || "",
        lastName: transformedUserData.lastName || "",
        email: transformedUserData.email || "",
        phone: transformedUserData.phone || "",
        bio: transformedUserData.bio || "",
      });
      setEditing(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await usersAPI.addAddress(newAddress);
      await loadAddresses();
      setNewAddress({
        addressLine1: "",
        addressLine2: "",
        city: "",
        region: "",
        country: "Guyana", // Always Guyana
        latitude: null,
        longitude: null,
        isDefault: false,
      });
      toast.success("Address added successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add address");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm("Are you sure you want to delete this address?"))
      return;

    try {
      await usersAPI.deleteAddress(id);
      await loadAddresses();
      toast.success("Address deleted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete address");
    }
  };

  const handleAddPreference = async (e) => {
    e.preventDefault();
    if (!newPreference.trim()) return;

    try {
      await usersAPI.addDietaryPreference({ name: newPreference });
      await loadDietaryPreferences();
      setNewPreference("");
      toast.success("Dietary preference added!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add preference");
    }
  };

  const handleDeletePreference = async (id) => {
    if (!window.confirm("Are you sure you want to delete this preference?"))
      return;

    try {
      await usersAPI.deleteDietaryPreference(id);
      await loadDietaryPreferences();
      toast.success("Preference deleted successfully!");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to delete preference"
      );
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await uploadAPI.uploadImage(formData);
      const imageUrl = response.data.data.url;

      // Update user profile with new image
      await usersAPI.updateProfile({ profileImage: imageUrl });
      updateUser({ ...user, profileImage: imageUrl });
      toast.success("Profile picture updated!");
    } catch (error) {
      toast.error("Failed to upload image");
    }
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  return (
    <Container>
      <Content>
        <Header>
          <ProfileHeader>
            <Avatar>
              {user.profileImage ? (
                <img src={user.profileImage} alt="Profile" />
              ) : (
                getInitials(user.firstName, user.lastName)
              )}
              <AvatarOverlay
                onClick={() => document.getElementById("imageUpload").click()}
              >
                📷
              </AvatarOverlay>
            </Avatar>
            <input
              id="imageUpload"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
            <UserInfo>
              <Name>
                {user.firstName} {user.lastName}
              </Name>
              <Email>{user.email}</Email>
              <Role>{user.role}</Role>
            </UserInfo>
          </ProfileHeader>

          <Tabs>
            <Tab
              $active={activeTab === "profile"}
              onClick={() => setActiveTab("profile")}
            >
              Profile Information
            </Tab>
            <Tab
              $active={activeTab === "addresses"}
              onClick={() => setActiveTab("addresses")}
            >
              Addresses
            </Tab>
            <Tab
              $active={activeTab === "preferences"}
              onClick={() => setActiveTab("preferences")}
            >
              Dietary Preferences
            </Tab>
          </Tabs>
        </Header>

        <TabContent>
          {activeTab === "profile" && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "2rem",
                }}
              >
                <h2>Profile Information</h2>
                <Button
                  onClick={() => setEditing(!editing)}
                  $variant={editing ? "secondary" : "primary"}
                >
                  {editing ? "Cancel" : "Edit Profile"}
                </Button>
              </div>

              <Form onSubmit={handleProfileUpdate}>
                <FormRow>
                  <FormGroup>
                    <Label>First Name</Label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={!editing}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Last Name</Label>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={!editing}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!editing}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phone</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!editing}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Bio</Label>
                  <TextArea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    disabled={!editing}
                    placeholder="Tell us about yourself..."
                  />
                </FormGroup>

                {editing && (
                  <Button type="submit" disabled={loading}>
                    {loading ? "Updating..." : "Update Profile"}
                  </Button>
                )}
              </Form>
            </div>
          )}

          {activeTab === "addresses" && (
            <div>
              <h2>Addresses</h2>

              {addresses.length > 0 ? (
                addresses.map((address) => (
                  <AddressCard
                    key={address.id}
                    className={address.isDefault ? "default" : ""}
                  >
                    <AddressHeader>
                      <AddressTitle>
                        Address
                        {address.isDefault && (
                          <DefaultBadge>Default</DefaultBadge>
                        )}
                      </AddressTitle>
                    </AddressHeader>
                    <AddressText>
                      {address.street}
                      {address.street2 && (
                        <>
                          <br />
                          {address.street2}
                        </>
                      )}
                      <br />
                      {address.city}, {address.state}, Guyana
                      {address.latitude && address.longitude && (
                        <>
                          <br />
                          <span
                            style={{ fontSize: "0.75rem", color: "#4caf50" }}
                          >
                            📍 GPS Location Available
                          </span>
                        </>
                      )}
                    </AddressText>
                    <AddressActions>
                      {address.latitude && address.longitude && (
                        <Button
                          $variant="primary"
                          onClick={() =>
                            window.open(
                              `https://www.google.com/maps?q=${address.latitude},${address.longitude}`,
                              "_blank"
                            )
                          }
                        >
                          View on Map
                        </Button>
                      )}
                      <Button
                        $variant="secondary"
                        onClick={() => handleDeleteAddress(address.id)}
                      >
                        Delete
                      </Button>
                    </AddressActions>
                  </AddressCard>
                ))
              ) : (
                <EmptyState>
                  <p>No addresses added yet.</p>
                </EmptyState>
              )}

              <h3>Add New Address</h3>
              <Form onSubmit={handleAddressSubmit}>
                <FormGroup>
                  <Label>📍 GPS Location (Recommended)</Label>
                  <LocationButton
                    type="button"
                    onClick={handleGetLocation}
                    disabled={gettingLocation}
                  >
                    <FiMapPin />
                    {gettingLocation
                      ? "Getting Location..."
                      : newAddress.latitude
                        ? "📍 Location Captured - Click to Update"
                        : "📍 Use My Current Location"}
                  </LocationButton>
                  {newAddress.latitude && newAddress.longitude && (
                    <LocationInfo>
                      <FiCheck />
                      Location: {newAddress.latitude.toFixed(6)},{" "}
                      {newAddress.longitude.toFixed(6)}
                    </LocationInfo>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Street Address</Label>
                  <Input
                    name="addressLine1"
                    value={newAddress.addressLine1}
                    onChange={handleAddressChange}
                    placeholder="Street address"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Address Line 2 (Optional)</Label>
                  <Input
                    name="addressLine2"
                    value={newAddress.addressLine2}
                    onChange={handleAddressChange}
                    placeholder="Apartment, suite, etc."
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label>City</Label>
                    <Input
                      name="city"
                      value={newAddress.city}
                      onChange={handleAddressChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Region/State</Label>
                    <Input
                      name="region"
                      value={newAddress.region}
                      onChange={handleAddressChange}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <label>
                    <input
                      type="checkbox"
                      name="isDefault"
                      checked={newAddress.isDefault}
                      onChange={handleAddressChange}
                    />
                    Set as default address
                  </label>
                </FormGroup>

                <Button type="submit" disabled={loading}>
                  {loading ? "Adding..." : "Add Address"}
                </Button>
              </Form>
            </div>
          )}

          {activeTab === "preferences" && (
            <div>
              <h2>Dietary Preferences</h2>

              {dietaryPreferences.length > 0 ? (
                dietaryPreferences.map((preference) => (
                  <DietaryPreference key={preference.id}>
                    <PreferenceName>{preference.name}</PreferenceName>
                    <DeleteButton
                      onClick={() => handleDeletePreference(preference.id)}
                    >
                      Delete
                    </DeleteButton>
                  </DietaryPreference>
                ))
              ) : (
                <EmptyState>
                  <p>No dietary preferences added yet.</p>
                </EmptyState>
              )}

              <h3>Add Dietary Preference</h3>
              <Form onSubmit={handleAddPreference}>
                <FormRow>
                  <FormGroup>
                    <Label>Preference Name</Label>
                    <Input
                      value={newPreference}
                      onChange={(e) => setNewPreference(e.target.value)}
                      placeholder="e.g., Vegetarian, Gluten-Free, etc."
                      required
                    />
                  </FormGroup>
                </FormRow>
                <Button type="submit">Add Preference</Button>
              </Form>
            </div>
          )}
        </TabContent>
      </Content>
    </Container>
  );
};

export default Profile;
